import Vue from 'vue'

import { EventService } from 'base/services/EventService'

import { Widget } from '..'
import { PermutatorService } from '../permutator'
import { DragDropService } from './DragDropService'
import { DragDropActionWidgetGetterFunction, DragDropCoords, DragDropEventCallbackFunction } from './DragDropTypes'

class DragDropAction {
  protected dragElementClone: HTMLElement | undefined
  protected dragElement: HTMLElement
  protected hintWindow: HTMLElement | null

  protected $onDragStart: DragDropEventCallbackFunction[] = []
  protected $onDrag: DragDropEventCallbackFunction[] = []
  protected $onDragEnd: DragDropEventCallbackFunction[] = []

  protected _mousedownHandler
  protected _dragEventHandler
  protected _dragEndEventHandler
  protected _onRowSpaceError
  protected _onRowSpaceSuccess

  protected waitingForMouseMoveToStartDragging: Boolean = false
  protected isTouch: Boolean = false
  protected isDragging: Boolean = false
  protected startPosition: DragDropCoords = { x: 0, y: 0 }
  protected relStartPosition: DragDropCoords = { x: 0, y: 0 }
  protected currentPosition: DragDropCoords = { x: 0, y: 0 }
  protected delta: DragDropCoords = { x: 0, y: 0 }
  protected widgetGetter: DragDropActionWidgetGetterFunction

  constructor(dragElement: HTMLElement, widgetGetter: DragDropActionWidgetGetterFunction) {
    this.dragElement = dragElement
    this.widgetGetter = widgetGetter
    this._dragEventHandler = this._onDrag.bind(this)
    this._dragEndEventHandler = this._onDragEnd.bind(this)
    this._onRowSpaceError = this.onRowSpaceError.bind(this)
    this._onRowSpaceSuccess = this.onRowSpaceSuccess.bind(this)
    this._mousedownHandler = this._onDragStart.bind(this)
  }

  setCoords(x: number, y: number) {
    this.currentPosition.x = x
    this.currentPosition.y = y

    this.delta.y = this.currentPosition.y - this.startPosition.y
    this.delta.x = this.currentPosition.x - this.startPosition.x
  }

  protected processEvent(event) {
    this.setCoords(
      this.isTouch ? event.touches[0].clientX : event.clientX,
      this.isTouch ? event.touches[0].clientY : event.clientY
    )
  }

  clearSelection() {
    let selection = window.getSelection()

    if (selection) {
      selection.removeAllRanges()
    }
  }

  protected notifyListeners(eventListeners: DragDropEventCallbackFunction[], isInitialCall = false) {
    eventListeners.forEach(listener => listener(this, isInitialCall))
  }

  protected _onDragEnd(event) {
    event.preventDefault()
    event.stopPropagation()

    document.removeEventListener(this.isTouch ? 'touchmove' : 'mousemove', this._dragEventHandler)
    document.removeEventListener(this.isTouch ? 'touchend' : 'mouseup', this._dragEndEventHandler)
    EventService.$off('error-row-not-enough-space', this._onRowSpaceError)
    EventService.$off('success-row-enough-space', this._onRowSpaceSuccess)

    if (!this.isDragging) {
      return
    }

    this.isDragging = false

    this.removeDragClone()

    if (this.hintWindow) {
      this.hideHint()
    }

    this.setCursor('default')

    this.getDragElement().removeAttribute('style')

    this.notifyListeners(this.$onDragEnd)
  }

  protected positionHintWindow() {
    let hint = this.hintWindow

    if (!hint) {
      return
    }

    let pos = this.getAbsolutePosition()
    hint.style.top = (pos.y + pageYOffset) + 'px'
    hint.style.left = (pos.x + pageXOffset + 10) + 'px'
  }

  protected createHintWindow() {
    let hint = document.createElement('div')
    hint.className = 'widget-board--generic-hint'

    this.hintWindow = hint

    let zIndex = 10000

    if (this.dragElementClone) {
      zIndex = parseInt(this.dragElementClone.style.zIndex, 10) + 1
    }

    hint.style.zIndex = String(zIndex)
    this.positionHintWindow()

    document.body.appendChild(hint)
  }

  showHint(hintText: string) {
    if (!this.hintWindow) {
      this.createHintWindow()
    }

    let hint = this.hintWindow!
    hint.innerText = hintText
  }

  hideHint() {
    if (!this.hintWindow) {
      return
    }
    document.body.removeChild(this.hintWindow)
    this.hintWindow = null
  }

  protected onRowSpaceError() {
    this.showHint('Nicht genügend Platz in der Zeile verfügbar')
    this.canItemBeDroppedHere(false)
  }

  protected onRowSpaceSuccess() {
    this.hideHint()
    this.canItemBeDroppedHere(true)
  }

  protected prepareWidgetContainer() {
    const widgetUUID = this.getWidget().wbUUID!
    const widgetContainerUUID = PermutatorService.getWidgetContainer(widgetUUID).wbUUID!
    DragDropService.selectContainer(widgetContainerUUID)
  }

  protected _initialiseDrag(event) {
    this.waitingForMouseMoveToStartDragging = false

    this.prepareDragClone()

    this.isDragging = true

    this.setCursor('grabbing')

    EventService.$on('error-row-not-enough-space', this._onRowSpaceError)
    EventService.$on('success-row-enough-space', this._onRowSpaceSuccess)

    this.prepareWidgetContainer()

    this.notifyListeners(this.$onDragStart)

    Vue.nextTick(() => this._onDrag(event, true))
  }

  protected _onDrag(event, isInitialCall = false) {
    if (this.waitingForMouseMoveToStartDragging) {
      this._initialiseDrag(event)
      return
    }

    this.processEvent(event)

    if (!this.isTouch) {
      event.preventDefault()
    }

    this.clearSelection()

    let pos = this.getAbsolutePosition()
    let relX = pos.x - this.relStartPosition.x + pageXOffset
    let relY = pos.y - this.relStartPosition.y + pageYOffset

    let $el = this.dragElementClone

    if ($el) {
      $el.style.top = relY + 'px'
      $el.style.left = relX + 'px'
    }

    if (this.hintWindow) {
      this.positionHintWindow()
    }

    this.notifyListeners(this.$onDrag, isInitialCall)
  }

  protected removeDragClone() {
    if (!this.dragElementClone) {
      return
    }

    document.body.removeChild(this.dragElementClone)
    document.body.removeAttribute('style')
    document.documentElement.removeAttribute('style')

    this.dragElementClone = undefined
  }

  protected prepareDragClone() {
    let dragElement = this.dragElement
    let dragClone: HTMLElement = dragElement.cloneNode(true) as HTMLElement

    dragClone.style.position = 'absolute'
    dragClone.style.transform = 'rotate(2deg)'
    dragClone.style.opacity = '0.8'
    dragClone.style.width = dragElement.offsetWidth + 'px'
    dragClone.style.height = dragElement.offsetHeight + 'px'
    dragClone.style.zIndex = '10000'

    const originalWidgetList = dragElement.querySelectorAll('.widget')
    const clonedWidgetList = dragClone.querySelectorAll('.widget')
    const numOriginalWidgets = originalWidgetList.length
    const numClonedWidgets = clonedWidgetList.length

    // make sure nested elements keep the original size
    let i = 0
    for (;i < numOriginalWidgets;i++) {
      const origWidget = originalWidgetList[i] as HTMLElement
      const origWbUUID = origWidget.getAttribute('data-wbuuid')

      let j = 0
      for (;j < numClonedWidgets;j++) {
        const clonedWidget = clonedWidgetList[j] as HTMLElement

        if (clonedWidget.getAttribute('data-wbuuid') !== origWbUUID) {
          continue
        }

        clonedWidget.style.width = origWidget.offsetWidth + 'px'
        clonedWidget.style.height = origWidget.offsetHeight + 'px'
        break
      }
    }

    this.dragElementClone = dragClone

    document.body.append(this.dragElementClone)

    // avoid infinite scrolling that occurs when scrollbars show up once an element is dragged outside the viewport
    document.body.style.position = 'relative'
    document.body.style.height = 'auto'
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflowY = 'auto'

    const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)

    // if the content doesn't fill up the whole screen dragged elements get cut off
    if (document.documentElement.offsetHeight < viewportHeight) {
      document.documentElement.style.height = viewportHeight + 'px'
      document.body.style.height = '100%'
    }
  }

  protected _onDragStart(event) {
    this.isTouch = event.type === 'touchstart'
    if (!this.isTouch && event.button !== 0) {
      return
    }

    this.initPosition(event)

    this.waitingForMouseMoveToStartDragging = true

    document.addEventListener(this.isTouch ? 'touchmove' : 'mousemove', this._dragEventHandler)
    document.addEventListener(this.isTouch ? 'touchend' : 'mouseup', this._dragEndEventHandler)

    // this is necessary to avoid multiple DragDropActions to react to this event
    // can happen if widgets are nested in a container
    event.preventDefault()
    event.stopPropagation()
  }

  protected initPosition(event) {
    const rect = this.dragElement.getBoundingClientRect()

    this.startPosition.x = this.isTouch ? event.touches[0].clientX : event.clientX
    this.startPosition.y = this.isTouch ? event.touches[0].clientY : event.clientY

    this.relStartPosition.x = this.startPosition.x - rect.left
    this.relStartPosition.y = this.startPosition.y - rect.top

    this.currentPosition = { x: 0, y: 0 }

    this.delta = { x: 0, y: 0 }
  }

  protected canItemBeDroppedHere(allowed: boolean) {
    if (!allowed) {
      this.setCursor('no-drop')
      return
    }

    if (this.isDragging) {
      this.setCursor('grabbing')
      return
    }

    this.setCursor('default')
  }

  protected setCursor(cursorName: string) {
    if (!this.dragElementClone) {
      return
    }
    this.dragElementClone.style.cursor = cursorName
  }

  getInitialPosition(): DragDropCoords {
    return this.startPosition
  }

  getAbsolutePosition(): DragDropCoords {
    return this.currentPosition
  }

  getRelativePosition(): DragDropCoords {
    return this.delta
  }

  getWidget(): Widget {
    return this.widgetGetter(this)
  }

  getDragElement(): HTMLElement {
    return this.dragElement
  }

  getWidgetType(): string {
    return this.getWidget().type
  }

  onDragStart(cb: DragDropEventCallbackFunction): DragDropAction {
    if (this.$onDragStart.includes(cb)) {
      return this
    }
    this.$onDragStart.push(cb)
    return this
  }

  onDrag(cb: DragDropEventCallbackFunction): DragDropAction {
    if (this.$onDrag.includes(cb)) {
      return this
    }
    this.$onDrag.push(cb)
    return this
  }

  onDragEnd(cb: DragDropEventCallbackFunction): DragDropAction {
    if (this.$onDragEnd.includes(cb)) {
      return this
    }
    this.$onDragEnd.push(cb)
    return this
  }

  listen() {
    this.dragElement.addEventListener('mousedown', this._mousedownHandler)
    this.dragElement.addEventListener('touchstart', this._mousedownHandler)
  }

  stop() {
    this.dragElement.removeEventListener('mousedown', this._mousedownHandler)
    this.dragElement.removeEventListener('touchstart', this._mousedownHandler)
  }
}

export { DragDropAction }
